import { useLocale as useLocaleR42 } from '@/lib/locale';
/**
 * WaiverAgreement — the compact "agree to the coach's Liability Waiver and
 * Cancellation Policy" block used by parent registration + cart checkout.
 *
 * Owner decision (2026-09-11): never show policy text inline. Each document
 * is a clickable link that opens the full text in a scrollable dialog; the
 * parent ticks ONE checkbox and moves on. Reading is their choice — the
 * signed packet (PDF) still contains the complete text.
 *
 * Props
 *   coachName          display name for the sentence ("… agree to Jane's …")
 *   waiverText         coach.waiver_text
 *   cancellation       { general, weather, sick } texts (any may be empty)
 *   agreed             boolean — single flag covering both documents
 *   onAgreedChange(v)  called with true/false
 *   testIdPrefix       e.g. "reg" | "cart" → `${prefix}-waiver-agree-checkbox`
 */
import React, { useState } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { FileText, CalendarX2, ExternalLink } from 'lucide-react';

function PolicyDialog({ open, onOpenChange, title, subtitle, sections, testId }) {
    const { t: t42 } = useLocaleR42();
    const filled = (sections || []).filter((s) => (s.text || '').trim());
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl" data-testid={testId}>
                <DialogHeader className="border-b border-[#0a1628]/10 bg-[#f4f7fb] px-6 pb-4 pt-6 text-left">
                    <DialogTitle className="font-display text-xl text-[#0a1628]">{title}</DialogTitle>
                    {subtitle && <DialogDescription className="text-sm text-[#1a3a5c]/80">{subtitle}</DialogDescription>}
                </DialogHeader>
                <ScrollArea className="flex-1 px-6 py-4">
                    {filled.length === 0 ? (
                        <p className="text-sm italic text-[#1a3a5c]/60" data-testid={`${testId}-empty`}> {t42("Your coach hasn't added this document yet — ask them directly for the specifics.")} </p>
                    ) : filled.map((s, i) => (
                        <section key={s.heading || i} className={i > 0 ? 'mt-5' : ''}>
                            {s.heading && <h4 className="font-display text-sm font-bold text-[#0a1628]">{s.heading}</h4>}
                            <pre className="mt-1 whitespace-pre-wrap font-sans text-sm leading-relaxed text-[#1a3a5c]">{s.text}</pre>
                        </section>
                    ))}
                </ScrollArea>
                <DialogFooter className="border-t border-[#0a1628]/10 bg-white px-6 py-3">
                    <Button type="button" onClick={() => onOpenChange(false)} className="sd-cta rounded-xl" data-testid={`${testId}-close`}>{t42("Done")}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export function WaiverAgreement({ coachName, waiverText, cancellation = {}, agreed, onAgreedChange, testIdPrefix = 'reg' }) {
    const { t: t42 } = useLocaleR42();
    const [openDoc, setOpenDoc] = useState(null); // 'waiver' | 'cancel' | null
    const who = coachName || 'your coach';
    const possessive = /s$/i.test(who) ? `${who}'` : `${who}'s`;
    const linkCls = 'font-semibold text-[#0a1628] underline decoration-[#00d4ff] decoration-2 underline-offset-2 hover:text-[#00a8cc] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00d4ff] rounded-sm';

    const docs = [
        { key: 'waiver', icon: FileText, label: 'Liability waiver', testId: `${testIdPrefix}-waiver-view-button` },
        { key: 'cancel', icon: CalendarX2, label: 'Cancellation policy', testId: `${testIdPrefix}-cancel-view-button` },
    ];

    return (
        <div data-testid={`${testIdPrefix}-waiver-agreement`}>
            <div className="grid gap-2 sm:grid-cols-2">
                {docs.map(({ key, icon: Icon, label, testId }) => (
                    <button
                        key={key}
                        type="button"
                        onClick={() => setOpenDoc(key)}
                        className="flex items-center gap-3 rounded-xl border border-[#0a1628]/10 bg-white px-3 py-2.5 text-left transition-colors hover:border-[#00d4ff] hover:bg-[#00d4ff]/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00d4ff]"
                        data-testid={testId}
                    >
                        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#00d4ff]/15 text-[#0a1628]"><Icon className="h-4 w-4" /></span>
                        <span className="min-w-0 flex-1">
                            <span className="block text-sm font-semibold text-[#0a1628]">{label}</span>
                            <span className="block text-xs text-[#1a3a5c]/70">{t42("Tap to read")}</span>
                        </span>
                        <ExternalLink className="h-4 w-4 shrink-0 text-[#1a3a5c]/50" />
                    </button>
                ))}
            </div>

            <label className="mt-3 flex cursor-pointer items-start gap-2.5 rounded-xl border border-[#00d4ff]/30 bg-[#e6fbff]/40 p-3 has-[[data-state=checked]]:border-[#00d4ff]">
                <Checkbox
                    checked={!!agreed}
                    onCheckedChange={(v) => onAgreedChange(v === true)}
                    className="mt-0.5"
                    data-testid={`${testIdPrefix}-waiver-agree-checkbox`}
                />
                <span className="text-sm text-[#0a1628]"> {t42("I agree to")} {possessive}{' '}
                    <button type="button" onClick={() => setOpenDoc('waiver')} className={linkCls} data-testid={`${testIdPrefix}-waiver-link`}>{t42("Liability Waiver")}</button>
                    {' '}{t42("and")}{' '}
                    <button type="button" onClick={() => setOpenDoc('cancel')} className={linkCls} data-testid={`${testIdPrefix}-cancel-link`}>{t42("Cancellation Policy")}</button>.
                </span>
            </label>

            <PolicyDialog
                open={openDoc === 'waiver'}
                onOpenChange={(o) => setOpenDoc(o ? 'waiver' : null)}
                title={t42("Liability waiver")}
                subtitle={`Between you and ${who}. You'll receive a signed PDF copy for your records.`}
                sections={[{ text: waiverText }]}
                testId={`${testIdPrefix}-waiver-dialog`}
            />
            <PolicyDialog
                open={openDoc === 'cancel'}
                onOpenChange={(o) => setOpenDoc(o ? 'cancel' : null)}
                title={t42("Cancellation policy")}
                subtitle={`How ${who} handles cancellations, weather and sick days.`}
                sections={[
                    { heading: 'General cancellations', text: cancellation.general },
                    { heading: 'Weather make-ups', text: cancellation.weather },
                    { heading: 'Sick & medical cancellations', text: cancellation.sick },
                ]}
                testId={`${testIdPrefix}-cancel-dialog`}
            />
        </div>
    );
}

export default WaiverAgreement;
